// executePurchaseOrderItemsImport.js
//
// Persist a purchase-order-items import plan under a Project.

import { createNode as createNodeFromFactory } from 'utilities/NodeFactory';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';
import { stampExclusiveAncestry } from 'utilities/nodeAncestry';
import { upsertEdge } from 'services/nodes/NodeEdgeService';
import { getNode } from 'services/nodes/NodeReadService';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { setNodeDocs, updateNode } from 'services/nodes/NodeWriteService';
import {
  PRODUCT_TYPE_KEY,
  PURCHASE_ORDER_ITEM_TYPE_KEY,
  PURCHASE_ORDER_TYPE_KEY,
  VENDOR_TYPE_KEY,
} from 'nodetypes/purchaseorderitem/import/purchaseOrderItemsImportUtils';

/**
 * @param {object} node
 * @param {string} sectionKey
 * @param {object} data
 * @returns {object}
 */
function mergeSection(node, sectionKey, data) {
  const existing = node?.[sectionKey] && typeof node[sectionKey] === 'object'
    ? { ...node[sectionKey] }
    : {};
  return {
    ...existing,
    ...(data || {}),
  };
}

/**
 * Deep-merge a data_* section onto an existing node and persist.
 *
 * @param {string} nodeKey
 * @param {string} sectionKey
 * @param {object} data
 * @param {object} user
 * @param {object} [extraPatch]
 * @returns {Promise<void>}
 */
async function updateNodeSection(nodeKey, sectionKey, data, user, extraPatch = {}) {
  const current = await getNode(nodeKey);
  const patch = {
    ...extraPatch,
    [sectionKey]: mergeSection(current || {}, sectionKey, data),
  };
  await updateNode(nodeKey, patch, { actor: user });
}

/**
 * Executes a planned denormalized PO-item import for a Project.
 *
 * @param {object} options
 * @param {object} options.plan
 * @param {object} options.projectNode
 * @param {object} options.user
 * @param {string|null} [options.baseKey]
 * @returns {Promise<object>}
 */
export async function executePurchaseOrderItemsImport({
  plan,
  projectNode,
  user,
  baseKey = null,
}) {
  if (!plan || !projectNode?.key || !user?.key) {
    throw new Error('Project, user, and import plan are required.');
  }

  const resolvedBaseKey = baseKey || projectNode.baseKey || null;
  const keyByTempId = new Map();

  const createEntries = [];
  const createdPayloadByTempId = new Map();

  // ----- Vendors -----
  for (const op of plan.vendors || []) {
    if (op.action === 'reuse' && op.key) {
      keyByTempId.set(op.tempId, op.key);
      continue;
    }
    if (op.action !== 'create') continue;

    const node = await createNodeFromFactory(VENDOR_TYPE_KEY, user, resolvedBaseKey);
    node.title = op.title;
    node.parentTitle = user.name || 'Profile';
    node.parentType = user.type || 'USER';
    node.profileRoot = true;
    node[op.sectionKey] = mergeSection(node, op.sectionKey, op.data);
    createEntries.push({ key: node.key, payload: node });
    createdPayloadByTempId.set(op.tempId, node);
    keyByTempId.set(op.tempId, node.key);
  }

  // ----- Products -----
  for (const op of plan.products || []) {
    if (op.action === 'update' && op.key) {
      keyByTempId.set(op.tempId, op.key);
      await updateNodeSection(
        op.key,
        op.sectionKey,
        op.data || {},
        user,
        op.title ? { title: op.title } : {},
      );
      continue;
    }
    if (op.action !== 'create') continue;

    const node = await createNodeFromFactory(PRODUCT_TYPE_KEY, user, resolvedBaseKey);
    node.title = op.title;
    node.parentTitle = user.name || 'Profile';
    node.parentType = user.type || 'USER';
    node.profileRoot = true;
    node[op.sectionKey] = mergeSection(node, op.sectionKey, op.data);
    createEntries.push({ key: node.key, payload: node });
    createdPayloadByTempId.set(op.tempId, node);
    keyByTempId.set(op.tempId, node.key);
  }

  // ----- Purchase orders -----
  for (const op of plan.purchaseOrders || []) {
    const vendorKey = op.vendorKey
      || (op.vendorTempId ? keyByTempId.get(op.vendorTempId) : null)
      || null;
    const data = { ...(op.data || {}) };
    if (vendorKey) data.vendor = [vendorKey];

    if (op.action === 'update' && op.key) {
      keyByTempId.set(op.tempId, op.key);
      await updateNodeSection(op.key, op.sectionKey, data, user);
      await upsertEdge(projectNode.key, op.key, { baseKey: resolvedBaseKey });
      continue;
    }
    if (op.action !== 'create') continue;

    let node = await createNodeFromFactory(PURCHASE_ORDER_TYPE_KEY, user, resolvedBaseKey);
    node.title = op.title;
    node.parentTitle = projectNode.title || '';
    node.parentType = projectNode.type || null;
    node.profileRoot = false;
    node.contributorKey = user.key;
    node[op.sectionKey] = mergeSection(node, op.sectionKey, data);
    node = stampExclusiveAncestry(node, projectNode, getNodeType(PURCHASE_ORDER_TYPE_KEY));
    createEntries.push({ key: node.key, payload: node });
    createdPayloadByTempId.set(op.tempId, node);
    keyByTempId.set(op.tempId, node.key);
  }

  // ----- Purchase order items -----
  for (const op of plan.items || []) {
    const poKey = keyByTempId.get(op.poTempId);
    const productKey = keyByTempId.get(op.productTempId);
    const vendorKey = op.vendorTempId ? keyByTempId.get(op.vendorTempId) : null;
    if (!poKey) {
      throw new Error(`Missing purchase order for row ${op.rowNumber}.`);
    }

    const data = { ...(op.data || {}) };
    if (productKey) data.product = [productKey];
    if (vendorKey) data.vendor = [vendorKey];

    if (op.action === 'update' && op.key) {
      keyByTempId.set(op.tempId, op.key);
      await updateNodeSection(
        op.key,
        op.sectionKey,
        data,
        user,
        { title: op.title },
      );
      await upsertEdge(poKey, op.key, { baseKey: resolvedBaseKey });
      continue;
    }
    if (op.action !== 'create') continue;

    const poNode = createdPayloadByTempId.get(op.poTempId) || { key: poKey, type: PURCHASE_ORDER_TYPE_KEY };
    let node = await createNodeFromFactory(
      PURCHASE_ORDER_ITEM_TYPE_KEY,
      user,
      resolvedBaseKey,
    );
    node.title = op.title;
    node.parentTitle = poNode.title || '';
    node.parentType = poNode.type || PURCHASE_ORDER_TYPE_KEY;
    node.profileRoot = false;
    node.contributorKey = user.key;
    node[op.sectionKey] = mergeSection(node, op.sectionKey, data);
    node = stampExclusiveAncestry(node, poNode, getNodeType(PURCHASE_ORDER_ITEM_TYPE_KEY));
    createEntries.push({ key: node.key, payload: node });
    createdPayloadByTempId.set(op.tempId, node);
    keyByTempId.set(op.tempId, node.key);
  }

  if (createEntries.length) {
    await setNodeDocs(createEntries, { merge: false });
  }

  // Edges for created POs and items
  for (const op of plan.purchaseOrders || []) {
    if (op.action !== 'create') continue;
    const poKey = keyByTempId.get(op.tempId);
    const poPayload = createdPayloadByTempId.get(op.tempId);
    if (!poKey) continue;
    await upsertEdge(
      projectNode.key,
      poKey,
      buildHierarchyEdgeOptions({
        parentNode: projectNode,
        childNode: poPayload || { key: poKey, type: PURCHASE_ORDER_TYPE_KEY },
        baseKey: resolvedBaseKey,
      }),
    );
  }

  for (const op of plan.items || []) {
    if (op.action !== 'create') continue;
    const poKey = keyByTempId.get(op.poTempId);
    const itemKey = keyByTempId.get(op.tempId);
    const poPayload = createdPayloadByTempId.get(op.poTempId)
      || { key: poKey, type: PURCHASE_ORDER_TYPE_KEY };
    const itemPayload = createdPayloadByTempId.get(op.tempId)
      || { key: itemKey, type: PURCHASE_ORDER_ITEM_TYPE_KEY };
    if (!poKey || !itemKey) continue;
    await upsertEdge(
      poKey,
      itemKey,
      buildHierarchyEdgeOptions({
        parentNode: poPayload,
        childNode: itemPayload,
        baseKey: resolvedBaseKey,
      }),
    );
  }

  return {
    createdNodeCount: createEntries.length,
    keyByTempId,
  };
}
